import * as Yup from "yup";
import { AnyObject } from "yup/lib/types";
import Big from "big.js";
import {
  CRYPTO_PLACES,
  decimalPlaces,
} from "@appComponents/CurrencyElement/CurrencyElement";
import { MAIN_CURRENCY } from "@bundles/consts";
import {
  getWalletFee,
  getWalletFeeObject,
  validateDecimalPlaces,
  validateMinimumWithdraw,
} from "@funds/features/helpers/validation";
import { countDecimalPlaces } from "@features/utils/functions";

const withdrawAmountSchema = Yup.object().shape({
  selectedCryptoWallet: Yup.object().required('Please select a bank account from the list'),
  amount: Yup.number()
    .required("Required field.")
    .positive("Withdrawal amount must be greater than 0.")
    .test(
      "decimalPlaces",
      "Invalid number of decimal places.",
      (value, options) => {
        const {
          parent: { userWallet },
        } = options as AnyObject;

        const isMainCurrency = userWallet.currency === MAIN_CURRENCY;

        const decimalPlacesLimit = isMainCurrency
          ? decimalPlaces[MAIN_CURRENCY]
          : CRYPTO_PLACES;

        const decimalPlacesAmount = countDecimalPlaces(value ?? 0);

        return (
          typeof validateDecimalPlaces(
            decimalPlacesAmount,
            decimalPlacesLimit
          ) !== "string"
        );
      }
    )

    .test(
      "feeAmount",
      "Withdraw amount must be greater than the fee.",
      (value) => {
        const fiatTransactionFee =
        Number(value) <= 10_000
          ? 100
          : Number(value) * 0.01;

        return Yup.number().min(fiatTransactionFee).isValidSync(value);
      }
    )
    .test("minimumWithdraw", (value, options) => {
      const {
        parent: { userWallet, fees, amount },
        createError,
        path,
      } = options as AnyObject;

      const currencyLabel = userWallet.currency === 'bfusd' ? 'usdt' : userWallet.currency;

      const selectedWalletFeeObject = fees?.find(
        (fee: any) => fee.currency === userWallet.currency
      );
      const defaultMinimumAmount =
        fees?.find((fee: any) => fee.currency === "default")?.minimum_amount ||
        0;
      const exchangeRate = null;
      const defaultMinimumAmountSetting = exchangeRate
        ? Big(defaultMinimumAmount).div(exchangeRate)
        : 0;

      const minimumAmountSetting =
        selectedWalletFeeObject &&
        selectedWalletFeeObject.currency !== "default"
          ? selectedWalletFeeObject.minimum_amount
          : defaultMinimumAmountSetting;

      const isMainCurrency = userWallet.currency === MAIN_CURRENCY;

      const decimalPlacesLimit = isMainCurrency
        ? decimalPlaces[MAIN_CURRENCY]
        : CRYPTO_PLACES;

      const minimum = Big(minimumAmountSetting)
        .round(decimalPlacesLimit);

      const validationMsg = validateMinimumWithdraw(
        value ?? 0,
        minimum,
        currencyLabel
      );

      if (!!amount && Big(amount).lt(minimum)) {
        return createError({
          message: "The entered amount is below the minimum required for this withdrawal type.",
          path,
        });
      }

      const hasMinimumWithdraw = minimum.lte(userWallet.balance);

      if (!hasMinimumWithdraw) {
        return createError({
          message: "Your available balance is not sufficient for this withdrawal.",
          path,
        });
      }

      if (typeof validationMsg === "string") {
        return createError({
          message: validationMsg,
          path,
        });
      }

      return true;
    })

    .test(
      "balance",
      "Your available balance is not sufficient for this withdrawal.",
      (value, options) => {
        const {
          parent: { userWallet },
        } = options as AnyObject;

        var walletBalance = new Big(userWallet.balance);
        var inputValue = new Big(value ?? 0);

        return userWallet ? inputValue.lte(walletBalance) : false;
      }
    ),
});


export default withdrawAmountSchema;
